import { useCallback } from "react";
import { apiFetch } from "@/shared/api";
import { useChatStoreApi } from "../stores/chat-store-context";

/**
 * 👍/👎 actions for an assistant turn. Optimistic-update the store first
 * so the button reacts instantly, roll back if the server returns an
 * error. The server mirrors the score to Langfuse fire-and-forget; we
 * don't wait on the third-party round-trip here either.
 */
export function useFeedback(sessionId: string) {
  const storeApi = useChatStoreApi();

  const submit = useCallback(
    async (traceId: string, score: 1 | -1, comment?: string) => {
      const note = comment?.trim() ? comment.trim() : null;
      const prev = storeApi.getState().messages.find(
        (m) => m.traceId === traceId,
      )?.feedback ?? null;
      storeApi.getState().setFeedbackForTrace(traceId, { score, comment: note });
      try {
        const resp = await apiFetch(
          `/api/sessions/${encodeURIComponent(sessionId)}/feedback`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ trace_id: traceId, score, comment: note }),
          },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      } catch (err) {
        storeApi.getState().setFeedbackForTrace(traceId, prev);
        console.error("Feedback submit failed:", err);
      }
    },
    [sessionId, storeApi],
  );

  const clear = useCallback(
    async (traceId: string) => {
      const prev = storeApi.getState().messages.find(
        (m) => m.traceId === traceId,
      )?.feedback ?? null;
      storeApi.getState().setFeedbackForTrace(traceId, null);
      try {
        const resp = await apiFetch(
          `/api/sessions/${encodeURIComponent(sessionId)}/feedback?trace_id=${encodeURIComponent(traceId)}`,
          { method: "DELETE" },
        );
        if (!resp.ok && resp.status !== 204) throw new Error(`HTTP ${resp.status}`);
      } catch (err) {
        storeApi.getState().setFeedbackForTrace(traceId, prev);
        console.error("Feedback clear failed:", err);
      }
    },
    [sessionId, storeApi],
  );

  return { submit, clear };
}
